import {FullStory, init, isInitialized, type SnippetOptions} from '@fullstory/browser';
import {storeToRefs} from 'pinia';
import {watch} from 'vue';
import {defineNuxtPlugin, useRoute, useRuntimeConfig} from '#app';
import {useUserStore} from '~/store/user';
import type {AuthUserCompany} from '~~/api/models';

type FullStoryUserProperties = Record<string, string | number | boolean>;
type FullStoryUserPropertySource = Record<string, string | number | boolean | null | undefined>;
interface RoleIdentityProperties {
  role?: string;
  companyRoles?: string;
}

const optionalString = (value?: string | null) => {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
};

const compactProperties = (properties: FullStoryUserPropertySource): FullStoryUserProperties => {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== null && value !== undefined && value !== '')
  ) as FullStoryUserProperties;
};

const getCompanyRoleName = (company: AuthUserCompany): string => {
  return optionalString(company.roleContextRoleNormalizedName) ?? 'NOT_SET';
};

const getCompanyDisplayName = (company: AuthUserCompany): string => {
  return optionalString(company.companyDisplayName)
    ?? optionalString(company.companyName)
    ?? optionalString(company.companySerialNumber)
    ?? 'Unknown company';
};

const getRoleIdentityProperties = (companies: AuthUserCompany[]): RoleIdentityProperties => {
  const companyRoles = companies.map(company => ({
    companyName: getCompanyDisplayName(company),
    role: getCompanyRoleName(company)
  }));

  if (companyRoles.length === 0) return {};

  const uniqueRoles = new Set(companyRoles.map(company => company.role));
  if (uniqueRoles.size === 1) {
    return {
      role: companyRoles[0]?.role ?? 'NOT_SET'
    };
  }

  return {
    companyRoles: companyRoles
      .map(company => `${company.companyName}: ${company.role}`)
      .join(' | ')
  };
};

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const route = useRoute();
  const fullstoryConfig = config.public.fullstory;
  const orgId = optionalString(fullstoryConfig.orgId);

  if (!fullstoryConfig.enabled || !orgId) return;

  const initOptions: SnippetOptions = {
    orgId,
    debug: fullstoryConfig.debug,
    appHost: optionalString(fullstoryConfig.appHost),
    host: optionalString(fullstoryConfig.host),
    script: optionalString(fullstoryConfig.script),
    cookieDomain: optionalString(fullstoryConfig.cookieDomain)
  };

  const ensureInitialized = () => {
    if (isInitialized()) return;

    init(initOptions);
  };

  if (!isInvitationRoute(route.path)) {
    ensureInitialized();
  }

  watch(
    () => route.path,
    (path) => {
      if (isInvitationRoute(path)) {
        if (isInitialized()) {
          FullStory('shutdown');
        }
        return;
      }

      ensureInitialized();
      FullStory('restart');
    }
  );

  const userStore = useUserStore();
  const {activeOrganisation, isAuthenticated, user, userCompanies, userIsFetched} = storeToRefs(userStore);

  let identifiedUid: string | null = null;
  let lastIdentitySignature: string | null = null;

  watch(
    () => {
      const currentUser = user.value;
      const activeOrganisationName = currentUser.organisations?.find(
        organisation => organisation.id === activeOrganisation.value
      )?.name;

      return {
        isAuthenticated: isAuthenticated.value,
        userIsFetched: userIsFetched.value,
        uid: currentUser.id,
        displayName: currentUser.displayName,
        email: currentUser.email,
        language: currentUser.language,
        activeOrganisationId: activeOrganisation.value,
        activeOrganisationName,
        organisationCount: currentUser.organisations?.length ?? 0,
        companyCount: currentUser.companies?.length ?? 0,
        ...getRoleIdentityProperties(userCompanies.value),
        isServiceUser: currentUser.isServiceUser,
        twoFactorEnabled: currentUser.twoFactorEnabled,
        isMobile: true,
        path: route.path
      };
    },
    (identityState) => {
      if (isInvitationRoute(identityState.path) || !isInitialized()) return;

      if (!identityState.isAuthenticated || !identityState.userIsFetched || !identityState.uid) {
        if (identifiedUid) {
          FullStory('setIdentity', {anonymous: true});
          identifiedUid = null;
          lastIdentitySignature = null;
        }

        return;
      }

      const identityPayload = {
        uid: identityState.uid,
        properties: compactProperties({
          displayName: identityState.displayName,
          email: identityState.email,
          language: identityState.language,
          activeOrganisationId: identityState.activeOrganisationId,
          activeOrganisationName: identityState.activeOrganisationName,
          organisationCount: identityState.organisationCount,
          companyCount: identityState.companyCount,
          role: identityState.role,
          companyRoles: identityState.companyRoles,
          isServiceUser: identityState.isServiceUser,
          twoFactorEnabled: identityState.twoFactorEnabled,
          isMobile: identityState.isMobile
        })
      };
      const identitySignature = JSON.stringify(identityPayload);

      if (identitySignature === lastIdentitySignature) return;

      if (identifiedUid === identityPayload.uid) {
        FullStory('setProperties', {
          type: 'user',
          properties: identityPayload.properties
        });
      } else {
        FullStory('setIdentity', identityPayload);
        identifiedUid = identityPayload.uid;
      }

      lastIdentitySignature = identitySignature;
    },
    {immediate: true}
  );
});

function isInvitationRoute(path: string): boolean {
  return path === '/invitation/expired' || path.startsWith('/invitation/');
}